import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { taskAPI, facilityAPI, documentAPI } from '../services/api';
import { Upload, MessageSquare, ArrowRight, AlertTriangle, Clock, CheckCircle, Building2, FlaskConical, FileOutput, Calculator, TrendingUp, CalendarDays } from 'lucide-react';
import { toast } from 'react-toastify';

function Dashboard() {
  const [stats, setStats] = useState({ facilities: 0, pendingTasks: 0, overdueTasks: 0, completedTasks: 0 });
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [emissionAlerts, setEmissionAlerts] = useState([]);
  const [emissionTotals, setEmissionTotals] = useState([]);
  const [emissionSummary, setEmissionSummary] = useState({ approaching: 0, within: 0 });
  const [facilityWatch, setFacilityWatch] = useState({ alerts: [], healthyCount: 0 });
  const [documentsStatus, setDocumentsStatus] = useState({ total: 0, analyzed: 0, missing: 0, facilityStatus: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboardData(); }, []);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days > 365) return '';
    return `${days} days ago`;
  };

  const loadDashboardData = async () => {
    try {
      const [taskStatsRes, facilitiesRes, documentsRes, tasksRes, emissionRes] = await Promise.all([
        taskAPI.getStats(),
        facilityAPI.getAll(),
        documentAPI.getAll(),
        taskAPI.getAll({}),
        api.get('/dashboard/emission-alerts').catch(() => ({ data: { alerts: [], totals: [], approaching: 0, within: 0 } })),
      ]);

      const s = taskStatsRes.data.stats;
      const facilitiesList = facilitiesRes.data.facilities || [];
      const docs = documentsRes.data.documents || [];

      setStats({
        facilities: facilitiesList.length,
        pendingTasks: parseInt(s.pending || 0) + parseInt(s.in_progress || 0),
        overdueTasks: parseInt(s.overdue || 0),
        completedTasks: parseInt(s.completed || 0),
      });

      setRecentTasks(tasksRes.data.tasks.slice(0, 5));
      setEmissionAlerts(emissionRes.data.alerts || []);
      setEmissionTotals(emissionRes.data.totals || []);
      setEmissionSummary({ approaching: emissionRes.data.approaching || 0, within: emissionRes.data.within || 0 });

      // Activity feed
      const activity = [];
      docs.filter(d => d.analyzed).slice(0, 2).forEach(doc => {
        const t = timeAgo(doc.upload_date || doc.created_at);
        if (t) activity.push({ color: '#16A34A', text: `Permit analyzed — ${(doc.document_name || '').replace('.pdf','').slice(0,40)}`, time: t });
      });
      (tasksRes.data.tasks || []).filter(t => t.status === 'overdue').forEach(t => {
        const ago = timeAgo(t.due_date);
        if (ago) activity.push({ color: '#DC2626', text: `Task overdue — ${t.title}`, time: ago });
      });
      (tasksRes.data.tasks || []).filter(t => t.status === 'completed').slice(0, 2).forEach(t => {
        const ago = timeAgo(t.updated_at);
        if (ago) activity.push({ color: '#16A34A', text: `Task completed — ${t.title}`, time: ago });
      });
      setRecentActivity(activity.slice(0, 5));

      // Documents status per facility
      const facilityStatus = facilitiesList.map(f => {
        const fDocs = docs.filter(d => d.facility_id === f.id);
        const analyzed = fDocs.filter(d => d.analyzed).length;
        return {
          id: f.id,
          name: f.facility_name || f.name,
          total: fDocs.length,
          analyzed,
          status: fDocs.length === 0 ? 'missing' : analyzed === 0 ? 'not_analyzed' : 'ok',
        };
      });
      setDocumentsStatus({ total: docs.length, analyzed: docs.filter(d => d.analyzed).length, missing: facilityStatus.filter(f => f.status === 'missing').length, facilityStatus });

      // Facility watch
      if (facilitiesList.length > 0) {
        const scoreResults = await Promise.all(
          facilitiesList.map(f =>
            api.get(`/audit/run/${f.id}`)
              .then(r => ({ id: f.id, name: f.facility_name || f.name, score: r.data.score || 0 }))
              .catch(() => ({ id: f.id, name: f.facility_name || f.name, score: 0 }))
          )
        );
        setFacilityWatch({
          alerts: scoreResults.filter(f => f.score < 85).sort((a, b) => a.score - b.score),
          healthyCount: scoreResults.filter(f => f.score >= 85).length,
        });
      }

    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getTaskBarColor = (priority, status) => {
    if (status === 'overdue') return '#EF4444';
    return { critical: '#EF4444', high: '#F59E0B', medium: '#0066FF', low: '#94a3b8' }[priority] || '#94a3b8';
  };

  const getBadgeStyle = (priority, status) => {
    if (status === 'overdue') return { background: '#FEF2F2', color: '#DC2626' };
    return ({
      critical: { background: '#FEF2F2', color: '#DC2626' },
      high: { background: '#FFFBEB', color: '#D97706' },
      medium: { background: '#EFF6FF', color: '#0066FF' },
      low: { background: '#F8FAFC', color: '#64748b' },
    })[priority] || { background: '#F8FAFC', color: '#64748b' };
  };

  const getDueBadgeText = (task) => {
    if (task.status === 'overdue') return 'Overdue';
    if (!task.due_date) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(task.due_date); due.setHours(0,0,0,0);
    const days = Math.round((due - today) / 86400000);
    if (days < 0) return 'Overdue';
    if (days === 0) return 'Today';
    if (days === 1) return '1d';
    if (days < 30) return `${days}d`;
    return `${Math.round(days/30)}mo`;
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user.fullName?.split(' ')[0] || 'there';
  const todayStr = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const emissionAlerting = emissionAlerts.filter(a => a.pct_used >= 50);

  const quickActions = [
    { to: '/documents/upload', icon: Upload, iconBg: '#EFF6FF', iconColor: '#0066FF', label: 'Upload Permit', sub: 'AI analyzes and auto-creates tasks' },
    { to: '/chat/new', icon: MessageSquare, iconBg: '#F0FDF4', iconColor: '#16A34A', label: 'AI Compliance Chat', sub: 'Ask any compliance question' },
    { to: '/bact-laer', icon: FlaskConical, iconBg: '#FFFBEB', iconColor: '#D97706', label: 'BACT / LAER / RACT', sub: 'New source review analysis' },
    { to: '/documents/generator', icon: FileOutput, iconBg: '#F5F3FF', iconColor: '#7C3AED', label: 'Generate Document', sub: '18 compliance document types' },
    { to: '/emission-factors', icon: Calculator, iconBg: '#F0FDF4', iconColor: '#16A34A', label: 'Emission Factor Library', sub: 'Look up EPA AP-42 factors' },
  ];

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>
  );

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <div className="greeting">{greeting}, {firstName} 👋</div>
        <div className="greeting-sub">
          {todayStr} · {stats.overdueTasks > 0 ? `${stats.overdueTasks} task${stats.overdueTasks > 1 ? 's' : ''} need immediate attention` : 'All caught up — great work!'}
        </div>
      </div>

      {/* Row 1: Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }} className="dashboard-stats-grid">
        {[
          { icon: AlertTriangle, iconBg: '#FEF2F2', iconColor: '#DC2626', value: stats.overdueTasks, label: 'Overdue', valueColor: stats.overdueTasks > 0 ? '#DC2626' : '#0A1628' },
          { icon: Clock, iconBg: '#FFFBEB', iconColor: '#D97706', value: stats.pendingTasks, label: 'Pending', valueColor: '#D97706' },
          { icon: Building2, iconBg: '#EFF6FF', iconColor: '#0066FF', value: stats.facilities, label: 'Facilities', valueColor: '#0066FF' },
          { icon: CheckCircle, iconBg: '#F0FDF4', iconColor: '#16A34A', value: stats.completedTasks, label: 'Completed', valueColor: '#16A34A' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ background: s.iconBg }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <s.icon size={14} color={s.iconColor} />
            </div>
            <div>
              <div className="stat-number" style={{ color: s.valueColor }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Row 2: Compliance Calendar | Emission Watch | Facility Watch */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }} className="dashboard-top-grid">

        {/* Compliance Calendar */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Compliance calendar</div>
            <Link to="/tasks" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              View all <ArrowRight size={13} />
            </Link>
          </div>
          {recentTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              <CalendarDays size={36} color="#CBD5E1" style={{ margin: '0 auto 12px', display: 'block' }} />
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, fontFamily: 'var(--font-body)' }}>Upload a permit to auto-generate tasks</div>
              <Link to="/documents/upload" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
                <Upload size={14} /> Upload Permit
              </Link>
            </div>
          ) : (
            <div>
              {recentTasks.map((task, i) => {
                const due = task.due_date ? new Date(task.due_date) : null;
                const today = new Date(); today.setHours(0,0,0,0);
                if (due) due.setHours(0,0,0,0);
                const days = due ? Math.round((due - today) / 86400000) : null;
                const isOverdue = task.status === 'overdue' || (days !== null && days < 0);
                const isUrgent = !isOverdue && days !== null && days <= 14;
                const badgeStyle = isOverdue ? { background: '#FEF2F2', color: '#DC2626' } : isUrgent ? { background: '#FFFBEB', color: '#D97706' } : { background: '#F1F5F9', color: '#64748b' };
                const badgeText = isOverdue ? 'Overdue' : days === 0 ? 'Today' : days === 1 ? '1d' : days !== null ? `${days}d` : '—';
                return (
                  <Link key={task.id} to={`/tasks?taskId=${task.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < recentTasks.length - 1 ? '1px solid #F1F5F9' : 'none', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {due ? (
                      <div style={{ width: 38, height: 38, borderRadius: 8, background: isOverdue ? '#FEF2F2' : isUrgent ? '#FFFBEB' : '#F8FAFC', border: `1px solid ${isOverdue ? '#FECACA' : isUrgent ? '#FDE68A' : '#E2E8F0'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: isOverdue ? '#DC2626' : isUrgent ? '#D97706' : '#64748b', textTransform: 'uppercase', lineHeight: 1 }}>{due.toLocaleDateString('en-US', { month: 'short' })}</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: isOverdue ? '#DC2626' : isUrgent ? '#D97706' : '#0A1628', lineHeight: 1.2, fontFamily: 'var(--font-heading)' }}>{due.getDate()}</div>
                      </div>
                    ) : (
                      <div style={{ width: 38, height: 38, borderRadius: 8, background: '#F8FAFC', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CalendarDays size={14} color="#94a3b8" />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#0A1628', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.title}</div>
                      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{task.facility_name ? `📍 ${task.facility_name}` : ''}</div>
                    </div>
                    <span className="badge" style={badgeStyle}>{badgeText}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Emission Watch */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Emission watch</div>
            <Link to="/sources" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>Sources →</Link>
          </div>
          {emissionTotals.length === 0 && emissionAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <TrendingUp size={28} color="#CBD5E1" style={{ margin: '0 auto 10px', display: 'block' }} />
              <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>No emissions logged yet.<br />Log emissions in Sources to track.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-body)', marginBottom: 2 }}>Rolling 12 months — {new Date().getFullYear()}</div>
              {emissionSummary.approaching > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8 }}>
                  <AlertTriangle size={13} color="#D97706" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#92400E', fontFamily: 'var(--font-body)', fontWeight: 500 }}>{emissionSummary.approaching} source{emissionSummary.approaching > 1 ? 's' : ''} approaching limit{emissionSummary.approaching > 1 ? 's' : ''}</span>
                </div>
              )}
              {emissionSummary.within > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }}>
                  <CheckCircle size={13} color="#16A34A" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#15803D', fontFamily: 'var(--font-body)', fontWeight: 500 }}>{emissionSummary.within} source{emissionSummary.within > 1 ? 's' : ''} within limits</span>
                </div>
              )}
              {emissionTotals.length > 0 && (
                <div style={{ padding: '10px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, fontFamily: 'var(--font-body)' }}>Total emissions</div>
                  {emissionTotals.map(({ pollutant, lbs }, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: 'var(--font-body)', marginBottom: i < emissionTotals.length - 1 ? 5 : 0 }}>
                      <span style={{ color: '#6B7280', fontWeight: 500 }}>{pollutant}</span>
                      <span style={{ color: '#0A1628', fontWeight: 700 }}>{Math.round(lbs).toLocaleString()} lbs</span>
                    </div>
                  ))}
                </div>
              )}
              {emissionAlerting.slice(0, 2).map((alert, i) => {
                const color = alert.pct_used >= 90 ? '#DC2626' : alert.pct_used >= 75 ? '#D97706' : '#0066FF';
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{alert.source_code ? `${alert.source_code} · ` : ''}{alert.pollutant}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color }}>{alert.pct_used}%</span>
                    </div>
                    <div style={{ height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: 4, width: `${Math.min(alert.pct_used, 100)}%`, background: color, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Facility Watch */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Facility watch</div>
            <Link to="/audit" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>Audit →</Link>
          </div>
          {stats.facilities === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13, fontFamily: 'var(--font-body)' }}>No facilities added yet</div>
          ) : facilityWatch.alerts.length === 0 && facilityWatch.healthyCount === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13, fontFamily: 'var(--font-body)' }}>Loading scores...</div>
          ) : (
            <div>
              {facilityWatch.alerts.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, marginBottom: 10 }}>
                  <CheckCircle size={14} color="#16A34A" />
                  <span style={{ fontSize: 13, color: '#15803D', fontFamily: 'var(--font-body)', fontWeight: 500 }}>All facilities in good standing</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  {facilityWatch.alerts.map((facility, i) => {
                    const color = facility.score >= 70 ? '#D97706' : '#DC2626';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#FAFAFA', borderRadius: 8, border: '1px solid #F1F5F9', cursor: 'pointer' }}
                        onClick={() => window.location.href = '/audit'}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: facility.score < 70 ? '#FEF2F2' : '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: 'var(--font-heading)' }}>{facility.score}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{facility.name}</div>
                          <div style={{ fontSize: 11, color, fontFamily: 'var(--font-body)', fontWeight: 500 }}>{facility.score < 70 ? 'Needs attention' : 'Review recommended'}</div>
                        </div>
                        <AlertTriangle size={14} color={color} />
                      </div>
                    );
                  })}
                </div>
              )}
              {facilityWatch.healthyCount > 0 && (
                <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CheckCircle size={12} color="#16A34A" />
                  {facilityWatch.healthyCount} facilit{facilityWatch.healthyCount > 1 ? 'ies' : 'y'} above 85
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Quick Actions | Recent Activity | Documents */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }} className="dashboard-bottom-grid">

        {/* Quick Actions */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Quick actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {quickActions.map(item => (
              <Link key={item.to} to={item.to} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 9, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0066FF'; e.currentTarget.style.background = '#FAFCFF'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = 'white'; }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <item.icon size={14} color={item.iconColor} />
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{item.label}</div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#6B7280', marginTop: 1 }}>{item.sub}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Recent activity</div>
          {recentActivity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13, fontFamily: 'var(--font-body)' }}>
              Activity will appear here as you use the app
            </div>
          ) : (
            <div>
              {recentActivity.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: i < recentActivity.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.text}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'var(--font-body)' }}>{item.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Documents</div>
            <Link to="/documents/upload" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {[
              { bg: '#EFF6FF', icon: '📄', label: 'Permits uploaded', value: documentsStatus.total, color: '#0066FF' },
              { bg: '#F0FDF4', icon: '✅', label: 'Analyzed by AI', value: documentsStatus.analyzed, color: '#16A34A' },
              { bg: '#FFFBEB', icon: '⚠️', label: 'Missing permits', value: documentsStatus.missing, color: '#D97706' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: s.bg, borderRadius: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>{s.icon}</div>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#374151', fontFamily: 'var(--font-body)', flex: 1 }}>{s.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: s.color, fontFamily: 'var(--font-heading)' }}>{s.value}</span>
              </div>
            ))}
          </div>
          {documentsStatus.facilityStatus.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8, fontFamily: 'var(--font-body)' }}>Facility status</div>
              {documentsStatus.facilityStatus.map((f, i) => {
                const color = f.status === 'ok' ? '#16A34A' : f.status === 'not_analyzed' ? '#D97706' : '#DC2626';
                const statusText = f.status === 'ok' ? `${f.total} permit${f.total > 1 ? 's' : ''} · analyzed` : f.status === 'not_analyzed' ? 'Not analyzed' : 'No permit';
                return (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < documentsStatus.facilityStatus.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 500, color, fontFamily: 'var(--font-body)', flexShrink: 0 }}>{statusText}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      <style>{`
        .dashboard-stats-grid { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; }
        .dashboard-top-grid { display: grid !important; grid-template-columns: 1fr 1fr 1fr !important; }
        .dashboard-bottom-grid { display: grid !important; grid-template-columns: 1fr 1fr 1fr !important; }
        @media (max-width: 768px) {
          .dashboard-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .dashboard-top-grid { grid-template-columns: 1fr !important; }
          .dashboard-bottom-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
